import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { EditReservationSheet, type StaffReservation } from '../../components/staff/EditReservationSheet';
import { PlaceAtTableSheet } from '../../components/staff/PlaceAtTableSheet';
import { CalendarPopup } from '../../components/CalendarPopup';
import { MaskIcon } from '../../components/Icon';
import { effectiveStatut, type ReservationStatut as Statut } from '../../utils/reservationStatus';
import { SHOW_PASS_VIEW } from '../../config';
import './Staff.css';
import './StaffResa.css';

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

interface Reservation {
  id: number;
  nom: string;
  telephone: string | null;
  email: string | null;
  date: string;
  heure: string;
  couverts: number;
  notes: string | null;
  service: 'midi' | 'soir' | null;
  statut: Statut | null;
  table_num: string | null;
}

const STATUS_LABEL: Record<Statut, string> = {
  confirme: 'confirmé',
  annule: 'annulé',
  termine: 'terminé',
  'no-show': 'no-show',
};

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** « Jeudi 24 juillet », capitalisé (toLocaleDateString rend le jour en bas de casse). */
function longDate(s: string): string {
  const out = parseYmd(s).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/** Admin gate : lit alterrego_user.isAdmin (peuplé depuis /auth). Jamais l'email. */
function isAdminUser(): boolean {
  try {
    const raw = localStorage.getItem('alterrego_user');
    return raw ? JSON.parse(raw).isAdmin === true : false;
  } catch {
    return false;
  }
}

function svcOf(r: Reservation): 'midi' | 'soir' {
  return r.service ?? (r.heure && parseInt(r.heure, 10) < 15 ? 'midi' : 'soir');
}

export function StaffResa() {
  const navigate = useNavigate();
  const admin = isAdminUser();
  const [selectedDate, setSelectedDate] = useState(ymd(new Date()));
  const [resas, setResas] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [capacity, setCapacity] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // sheet === undefined → fermé ; null → création ; StaffReservation → édition.
  const [sheet, setSheet] = useState<StaffReservation | null | undefined>(undefined);
  const [placing, setPlacing] = useState<Reservation | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchResas = useCallback(async (date: string) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/reservations?date=${date}`);
      if (res.ok) {
        const data = await res.json();
        setResas(Array.isArray(data) ? data : []);
      } else {
        setResas([]);
      }
    } catch {
      setResas([]);
    }
    setLoading(false);
  }, []);

  // Capacité du jour (V1 globale midi/soir) via /disponibilites.
  const fetchCapacity = useCallback(async (date: string) => {
    try {
      const res = await apiFetch(
        `/reservations/disponibilites?date=${date}&heure=20:00&couverts=1&service=soir`,
        { auth: false },
      );
      if (res.ok) {
        const d = await res.json();
        setCapacity(typeof d.capacity === 'number' ? d.capacity : null);
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchResas(selectedDate);
    fetchCapacity(selectedDate);
  }, [selectedDate, fetchResas, fetchCapacity]);

  // Le menu d'une card ne doit pas survivre à un changement de jour / de recherche.
  useEffect(() => { setMenuId(null); }, [selectedDate, search]);

  // Date strip (7 jours glissants depuis aujourd'hui). Le calendrier complet
  // permet d'en sortir : on ancre donc la bande sur la date sélectionnée dès
  // qu'elle tombe hors de cette fenêtre (sinon aucune puce n'est active).
  const dates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sel = parseYmd(selectedDate);
    const inWindow = sel >= today && (sel.getTime() - today.getTime()) / 86_400_000 < 7;
    const start = inWindow ? today : sel;
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [selectedDate]);

  // Statut effectif : « Terminé » est déduit de l'heure prévue (cf.
  // utils/reservationStatus), il n'est plus posé à la main.
  const withStatut = useMemo(
    () => resas.map((r) => ({ r, st: effectiveStatut(r) })),
    [resas],
  );

  // Occupation réelle (toutes résa non annulées) — indépendante des filtres d'affichage.
  const booked = useMemo(() => {
    const acc = { midi: 0, soir: 0 };
    for (const { r, st } of withStatut) {
      if (st === 'annule') continue;
      acc[svcOf(r)] += r.couverts;
    }
    return acc;
  }, [withStatut]);

  // Liste affichée : résas du jour non annulées + recherche (nom/tel/email).
  // Les annulées sortent de la vue staff (elles restent en base) ; « terminé »
  // est déduit de l'heure prévue et reste affiché.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return withStatut.filter(({ r, st }) => {
      if (st === 'annule') return false;
      if (!q) return true;
      const hay = `${r.nom ?? ''} ${r.telephone ?? ''} ${r.email ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [withStatut, search]);

  const midi = filtered.filter(({ r }) => svcOf(r) === 'midi');
  const soir = filtered.filter(({ r }) => svcOf(r) === 'soir');
  const totalCouverts = filtered.reduce((s, { r }) => s + r.couverts, 0);

  const flash = (kind: 'ok' | 'err', msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2200);
  };

  const setStatut = async (r: Reservation, statut: Statut) => {
    if (!admin) { flash('err', 'Action réservée à l’admin'); return; }
    setBusyId(r.id);
    try {
      const res = await apiFetch(`/reservations/${r.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut }),
      });
      if (res.ok) {
        flash('ok', `Réservation ${STATUS_LABEL[statut]}`);
        fetchResas(selectedDate);
      } else {
        flash('err', 'Échec de la mise à jour');
      }
    } catch {
      flash('err', 'Erreur réseau');
    }
    setBusyId(null);
  };

  const pct = (n: number) => (capacity ? Math.round((n / capacity) * 100) : 0);
  const fillClass = (n: number) => {
    const p = pct(n);
    if (p >= 100) return ' full';
    if (p >= 80) return ' warn';
    return '';
  };

  const renderCard = ({ r, st }: { r: Reservation; st: Statut }) => (
    <div key={r.id} className="resa-card" onClick={() => setSheet(r as StaffReservation)}>
      <div className="resa-card-row">
        <div className="resa-card-left">
          <span className="resa-time">{r.heure}</span>
        </div>
        <div className="resa-card-main">
          <strong>{r.nom}</strong>
          <span>
            {r.couverts} couvert{r.couverts > 1 ? 's' : ''}
            {r.telephone ? ` · ${r.telephone}` : ''}
          </span>
          {r.notes && <span className="resa-notes">{r.notes}</span>}
        </div>
        <div className="resa-card-right">
          {r.table_num && <span className="resa-table">T{r.table_num}</span>}
          <span className={`resa-status ${st}`}>{STATUS_LABEL[st]}</span>
        </div>
        {admin && (
          <ResaCardMenu
            open={menuId === r.id}
            busy={busyId === r.id}
            canCancel={st !== 'annule'}
            onToggle={() => setMenuId((cur) => (cur === r.id ? null : r.id))}
            onEdit={() => { setMenuId(null); setSheet(r as StaffReservation); }}
            onPlace={() => { setMenuId(null); setPlacing(r); }}
            onCancel={() => { setMenuId(null); setStatut(r, 'annule'); }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="staff-page">
      <div className="staff-header">
        <button className="staff-back" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>Reservations</h1>
        <div className="staff-header-actions">
          {/* Vue plein écran « pass » pour l'iPad mural (lecture rapide + actions).
              Masquée par SHOW_PASS_VIEW pendant la beta staff — cf. config.ts. */}
          {SHOW_PASS_VIEW && (
            <button className="staff-passview-btn" onClick={() => navigate('/staff/resa-du-jour')}>📺 Vue pass</button>
          )}
          <button className="staff-add-btn" onClick={() => setSheet(null)}>+ Ajouter</button>
        </div>
      </div>

      {/* Jour consulté — ouvre le calendrier complet (au-delà des 7 jours du
          swiper). Présenté comme la carte récap de l'écran client (icône
          calendrier + date + « Modifier ») pour rendre le tap évident. */}
      <button className="resa-day-btn" onClick={() => setShowCalendar(true)}>
        <MaskIcon name="calendar" size={18} color="var(--color-primary)" />
        <span className="resa-day-label">{longDate(selectedDate)}</span>
        <span className="resa-day-edit">Modifier</span>
      </button>

      {/* Date strip */}
      <div className="resa-date-strip">
        {dates.map((d) => {
          const ds = ymd(d);
          return (
            <button key={ds} className={`resa-date-chip ${ds === selectedDate ? 'active' : ''}`}
              onClick={() => setSelectedDate(ds)}>
              <span className="resa-date-dow">{DAYS_FR[d.getDay()]}</span>
              <span className="resa-date-num">{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      {/* Summary */}
      <div className="resa-summary">
        <span><strong>{filtered.length}</strong> resa{filtered.length > 1 ? 's' : ''}</span>
        <span><strong>{totalCouverts}</strong> couverts</span>
      </div>

      {/* Indicateur capacité midi / soir */}
      <div className="resa-capacity-row">
        <div className={`resa-cap-card${fillClass(booked.midi)}`}>
          <span className="resa-cap-label">🍽 Déjeuner</span>
          <span className="resa-cap-value">
            {booked.midi} / {capacity ?? '—'}{' '}
            <small>({pct(booked.midi)}%)</small>
          </span>
        </div>
        <div className={`resa-cap-card${fillClass(booked.soir)}`}>
          <span className="resa-cap-label">🍷 Dîner</span>
          <span className="resa-cap-value">
            {booked.soir} / {capacity ?? '—'}{' '}
            <small>({pct(booked.soir)}%)</small>
          </span>
        </div>
      </div>

      {/* Recherche */}
      <div className="resa-search-wrap">
        <input
          className="resa-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher nom, téléphone, email…"
        />
      </div>

      {toast && <div className={`resa-toast ${toast.kind}`}>{toast.msg}</div>}

      {/* List */}
      {loading ? (
        <div className="resa-loading">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="resa-empty">Aucune reservation</div>
      ) : (
        <div className="resa-list">
          {soir.length > 0 && <div className="resa-section-label">🍷 Diner ({soir.length})</div>}
          {soir.map(renderCard)}
          {midi.length > 0 && <div className="resa-section-label">🍽 Dejeuner ({midi.length})</div>}
          {midi.map(renderCard)}
        </div>
      )}

      {showCalendar && (
        <CalendarPopup
          today={new Date()}
          agenda={null}
          initialMonth={parseYmd(selectedDate)}
          allowPast
          onSelect={(d) => { setSelectedDate(ymd(d)); setShowCalendar(false); }}
          onClose={() => setShowCalendar(false)}
        />
      )}

      {placing && (
        <PlaceAtTableSheet
          reservation={placing}
          onClose={() => setPlacing(null)}
          onSaved={() => { flash('ok', 'Table enregistrée'); fetchResas(selectedDate); }}
        />
      )}

      {sheet !== undefined && (
        <EditReservationSheet
          reservation={sheet}
          defaultDate={selectedDate}
          onClose={() => setSheet(undefined)}
          onSaved={() => { fetchResas(selectedDate); fetchCapacity(selectedDate); }}
        />
      )}
    </div>
  );
}

/**
 * Menu d'actions d'une card (cahier beta : UN SEUL point d'entrée par card, en
 * haut à droite, trois actions maximum). Les statuts Confirmer / Terminer /
 * No-show ont disparu : confirmé est l'état par défaut, terminé est déduit du
 * temps, no-show reviendra par la vue « Bilan de service ».
 */
function ResaCardMenu({ open, busy, canCancel, onToggle, onEdit, onPlace, onCancel }: {
  open: boolean;
  busy: boolean;
  canCancel: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onPlace: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Fermeture au clic extérieur et à Échap. `pointerdown` (et non `click`) pour
  // que le menu soit refermé avant qu'un tap n'atteigne la card en dessous.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onToggle();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onToggle(); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onToggle]);

  return (
    <div className="resa-menu" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        className="resa-menu-btn"
        aria-label="Actions"
        aria-expanded={open}
        disabled={busy}
        onClick={onToggle}
      >⋯</button>
      {open && (
        <div className="resa-menu-pop" role="menu">
          <button role="menuitem" onClick={onEdit}>Modifier</button>
          <button role="menuitem" onClick={onPlace}>Placer à table</button>
          <button role="menuitem" className="danger" disabled={!canCancel} onClick={onCancel}>Annuler</button>
        </div>
      )}
    </div>
  );
}
